import { merge } from 'lodash-es';
import { type ComponentProps, type RefObject, useCallback, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { GutterContext, GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import { Divider } from '../../assets/Divider/Divider';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import { backgrounds } from '../../common/backgrounds';
import { sizing } from '../../common/sizing';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { type FadeEdge, fadeMaskCSS, overflowFadeMaskCSS } from '../../utilities/shared/FadeMask';
import { getGutterTokens, gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';
import {
  type ShellPropContentMode,
  type ShellPropSize,
  type ShellPropType,
  ShellLayerScrollProvider,
  useShellContentMode,
  useShellMaxWidth,
  useShellMinWidth,
  useShellSize,
  useShellType,
  useShellWidth,
  useShellWidthMode,
} from './utils';

const ShellContentDiagram = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentDiagram.displayName = 'ShellContentDiagram';

const ShellContentGlance = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentGlance.displayName = 'ShellContentGlance';

const ShellContentControls = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentControls.displayName = 'ShellContentControls';

export const ShellContentScroll = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'auto',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
        minHeight: 0,
      },
    },
  },
});
ShellContentScroll.displayName = 'ShellContentScroll';

const ShellContentInset = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  variants: {
    contentMode: {
      fit: {
        minHeight: 'min-content',
      },
      stretch: {
        flex: '1 1 auto',
        minHeight: 0,
      },
    },
    widthMode: {
      centered: {
        alignSelf: 'center',
      },
      full: {},
    },
  },

  defaultVariants: {
    contentMode: 'stretch',
  },
});
ShellContentInset.displayName = 'ShellContentInset';

const ShellContentStatic = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  height: 'fit-content',

  variants: {
    widthMode: {
      centered: {
        alignSelf: 'center',
      },
      full: {},
    },
  },
});
ShellContentStatic.displayName = 'ShellContentStatic';

const ShellContentPosition = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
      },
    },
    variant: {
      scroll: {},
      static: {
        flex: '1 0 auto',
        heightAll: 'min-content',
        // Unlike `hidden`, `clip` can pair with a visible y-axis, so controls can overhang the divider.
        overflowX: 'clip',
        overflowY: 'visible',

        '@notDesktop': {
          width: '100%',
        },
      },
    },
  },
});
ShellContentPosition.displayName = 'ShellContentPosition';

const ShellContentDivider = styled(Divider, {
  variants: {
    shellSize: {
      'x-small': {
        padding: `0 ${sizing.contentSides}`,
      },
      small: {
        padding: `0 ${sizing.contentSides}`,
      },
      medium: {
        padding: `0 ${sizing.sides}`,
      },
    },
  },
  defaultVariants: {
    shellSize: 'medium',
  },
});
ShellContentDivider.displayName = 'ShellContentDivider';

const shellContentPatternedBackground = {
  checkered: {
    background: backgrounds.checkeredLight,
    backgroundColor: colors.bgApplicationLight,

    [darkThemeSelector]: {
      background: backgrounds.checkeredDark,
      backgroundColor: colors.bgApplicationDark,
    },
  },
  dotted: {
    background: backgrounds.dottedLight,
    backgroundColor: colors.bgApplicationLight,

    [darkThemeSelector]: {
      background: backgrounds.dottedDark,
      backgroundColor: colors.bgApplicationDark,
    },
  },
};

const shellContentLoadingWashBackground = {
  ...shellContentPatternedBackground,
  default: {
    backgroundColor: colors.bgApplicationLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgApplicationDark,
    },
  },
};

const ShellContentContainer = styled('div', {
  gridArea: 'shell-content',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',

  variants: {
    background: {
      ...shellContentPatternedBackground,
      default: {},
    },
  },
});
ShellContentContainer.displayName = 'ShellContentContainer';

const ShellContentLoadingOverlay = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: zIndexes.contentLoading,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.8,

  variants: {
    background: shellContentLoadingWashBackground,
  },

  defaultVariants: {
    background: 'default',
  },
});
ShellContentLoadingOverlay.displayName = 'ShellContentLoadingOverlay';

export type ShellContentProps = {
  /**
   * Set whether the background should be checkered or not.
   */
  background?: 'default' | 'checkered' | 'dotted';
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * When true, covers the shell content (including controls and scroll area) with a loading wash
   * and spinner. The wash matches {@link ShellContentProps.background} when set.
   */
  loading?: boolean;
  /**
   * Set the content mode for the content.
   */
  contentMode?: ShellPropContentMode;
  /**
   * Provide controls for the content.
   */
  controls?: React.ReactNode;
  /**
   * Provide diagram for the content, which goes before controls.
   */
  diagram?: React.ReactNode;
  /**
   * Show a masking gradient at the edges of the container.
   * When `gutter`, the fade matches the `gutter` property.
   */
  fade?: FadeEdge | 'gutter';
  /**
   * Provide glance for the content, which goes after controls.
   */
  glance?: React.ReactNode;
  /**
   * Provide a size for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  size?: ShellPropSize;
  /**
   * Shell frame type for container queries (`container-name` / `containers.*` in atto).
   * Inherits from `Shell` via context when omitted. Pane/Drawer wrappers set this explicitly.
   */
  type?: ShellPropType;
  /**
   * Set the spacing between the children.
   */
  spacing?: SpacingScale;
  /**
   * Ref to the scroll container.
   */
  scrollRef?: RefObject<HTMLDivElement | null>;
} & GutterProp &
  Omit<ComponentProps<typeof ShellContentContainer>, 'type'>;

export function ShellContent({
  background,
  children,
  contentMode,
  controls,
  diagram,
  fade = 'none',
  glance,
  gutter = { edge: 'all' },
  loading,
  ref,
  scrollRef,
  size,
  spacing = 16,
  type,
  ...remaining
}: ShellContentProps) {
  const shellContentMode = useShellContentMode(contentMode);
  const shellSize = useShellSize(size, 'medium');
  const shellType = useShellType(type);
  const shellWidth = useShellWidth();
  const shellMaxWidth = useShellMaxWidth();
  const shellMinWidth = useShellMinWidth();
  const shellWidthMode = useShellWidthMode();
  const innerScrollRef = useRef<HTMLDivElement>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);

  const scrollContainerSyncRef = useCallback((node: HTMLDivElement | null) => {
    setScrollContainer(node);
  }, []);

  const shellContentSpacingRef = mergeRefs([scrollRef ?? innerScrollRef, scrollContainerSyncRef]);

  let gutterContext: GutterContext = 'application';
  if (shellSize === 'small' || shellSize === 'x-small') {
    gutterContext = 'content';
  }

  const { ends: fadeSizeToken } = getGutterTokens(gutterContext, gutter.edge);
  const propFade = fade === 'gutter' ? gutter.edge : fade;

  const hasWidth = shellWidth || shellMaxWidth || shellMinWidth;
  const isCentered = shellWidthMode === 'centered';

  const css = merge(
    shellWidth ? widthCSS(shellWidth) : undefined,
    shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
    shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
  );

  const shellContentCSS = merge(
    propFade === 'none'
      ? overflowFadeMaskCSS(fadeSizeToken, 'horizontal')
      : fadeMaskCSS(fadeSizeToken, propFade),
    isCentered && hasWidth ? undefined : css,
  );

  const shellContentInsetCSS = merge(
    spacingCSS(spacing),
    gutterCSS({ context: gutterContext, ...gutter }),
    isCentered && hasWidth ? css : undefined,
  );

  const { css: cssProp, ...restRemaining } = remaining;

  return (
    <ShellContentContainer
      ref={ref}
      background={background}
      aria-busy={loading ? true : undefined}
      css={merge(
        {
          containerType: 'inline-size',
          containerName: shellType,
        },
        cssProp,
      )}
      {...restRemaining}
    >
      {controls || glance || diagram ? (
        <ShellContentPosition variant="static">
          <ShellContentStatic widthMode={shellWidthMode} css={css}>
            {diagram && (
              <>
                <ShellContentDivider shellSize={shellSize} />
                <ShellContentDiagram>{diagram}</ShellContentDiagram>
              </>
            )}
            {controls && (
              <>
                <ShellContentDivider shellSize={shellSize} />
                <ShellContentControls>{controls}</ShellContentControls>
              </>
            )}
            {glance && (
              <>
                <ShellContentDivider shellSize={shellSize} />
                <ShellContentGlance>{glance}</ShellContentGlance>
              </>
            )}
            <ShellContentDivider shellSize={shellSize} />
          </ShellContentStatic>
        </ShellContentPosition>
      ) : null}
      <ShellContentPosition contentMode={shellContentMode}>
        <ShellLayerScrollProvider scrollContainer={scrollContainer}>
          <ShellContentScroll
            ref={shellContentSpacingRef}
            contentMode={shellContentMode}
            css={shellContentCSS}
          >
            <ShellContentInset
              contentMode={shellContentMode}
              widthMode={shellWidthMode}
              css={shellContentInsetCSS}
            >
              {children}
            </ShellContentInset>
          </ShellContentScroll>
        </ShellLayerScrollProvider>
      </ShellContentPosition>
      {loading ? (
        <ShellContentLoadingOverlay
          background={background ?? 'default'}
          role="status"
          aria-live="polite"
          aria-label="Loading"
        >
          <LoadingIcon size={20} variant="neutral" />
        </ShellContentLoadingOverlay>
      ) : null}
    </ShellContentContainer>
  );
}
